import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { SelectInput } from "../../Form/SelectInput";
import { DateTimePickerInput } from "../../Form/DateTimePickerInput";
import { TextAreaInput } from "../../Form/TextAreaInput";
import { PrimaryButton } from "../../Buttons/PrimaryButton";
import * as React from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useBoundStore } from "../../../Store/store";
import { useEffect, useState } from "react";
import { ControlledSwitch } from "../../Form/ControlledSwitch";
import dayjs from "dayjs";
import { NumberInput } from "../../Form/NumberInput";
import toast from "react-hot-toast";
import DatePicker from "react-datepicker";
import nl from "date-fns/locale/nl";

export const AdminCreateShiftDialog = (props) => {
    const {
        open,
        onClose,
        customerId = null,
        branchId = null,
        onCreated,
        customerBranches = [],
    } = props;

    const { t } = useTranslation();

    const createShift = useBoundStore((state) => state.createShift);
    const previewInterrupted = useBoundStore((state) => state.previewInterrupted);
    const getAllFreelancers = useBoundStore((state) => state.getAllFreelancers);
    const freelancers = useBoundStore((state) => state.freelancers);

    const [autoInterrupted, setAutoInterrupted] = useState(null);

    const shiftApplicationPossibilities = [
        { id: 1, value: 'both', label: t('shifts.both') },
        { id: 2, value: 'only_verpleegkundige', label: t('shifts.filter_only_nurse') },
        { id: 3, value: 'only_zorgkundige', label: t('shifts.filter_only_caregiver') },
    ];

    const statutenOptions = [
        { id: 1, value: 'freelance', label: t('statuten.freelance') },
        { id: 2, value: 'interim', label: t('statuten.interim') },
        { id: 3, value: 'student', label: t('statuten.student') },
        { id: 4, value: 'flexi_job', label: t('statuten.flexi_job') },
    ];

    const [repeatDates, setRepeatDates] = useState([]);
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        getAllFreelancers(
            null,
            null,
            true
        );
    }, [getAllFreelancers]);

    const schema = yup.object({
        branch_id: yup.number().required(),
        freelancer_id: yup.number().nullable(),
        start_time: yup.string().required(),
        end_time: yup
            .string()
            .required()
            .test(
                "is-after-start",
                "De eindtijd moet na de starttijd zijn",
                function (value) {
                    const { start_time } = this.parent;
                    if (!start_time || !value) return true;
                    return dayjs(value).isAfter(dayjs(start_time));
                }
            ),
        break_duration: yup.string().nullable(),
        shift_repeats_enabled: yup.boolean(),
        shift_repeats: yup.array().nullable(),
        shift_applications_possibility: yup.mixed().nullable(),
        allowed_statuten: yup.array().of(yup.string()).min(1, t('shifts_module.allowed_statuten_min')).required(),
        interrupted_shift: yup.boolean()
    }).required()

    const {
        register,
        handleSubmit,
        formState: { errors },
        getValues,
        setValue,
        control,
        watch,
        reset
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues: {
            branch_id: branchId !== null ? branchId : null,
            freelancer_id: null,
            start_time: null,
            end_time: null,
            break_duration: null,
            is_published: false,
            shift_repeats_enabled: false,
            shift_repeats: [],
            shift_applications_possibility: 'both',
            allowed_statuten: ['freelance'],
            interrupted_shift: false,
        }
    });

    useEffect(() => {
        if (open) {
            reset({
                branch_id: branchId !== null ? branchId : null,
                freelancer_id: null,
                start_time: null,
                end_time: null,
                break_duration: null,
                shift_repeats_enabled: false,
                shift_repeats: [],
                shift_applications_possibility: 'both',
                allowed_statuten: ['freelance'],
                interrupted_shift: false,
            });
            setRepeatDates([]);
            setIsCreating(false);
        }
    }, [open, branchId, reset]);

    useEffect(() => {
        reset({
            branch_id: branchId !== null ? branchId : null,
        })
    }, [branchId, reset]);

    const watchedRepeatShifts = watch('shift_repeats_enabled');
    const watchedPossibility = watch('shift_applications_possibility');
    const watchedFreelancerId = watch('freelancer_id');
    const watchedStart = watch('start_time');
    const watchedEnd = watch('end_time');
    const watchedInterrupted = watch('interrupted_shift');

    const showAsManual = autoInterrupted !== null && watchedInterrupted !== autoInterrupted;

    const matchesPossibility = (freelancer, possibility) => {
        if (!possibility || possibility === 'both') return true;
        const fn = freelancer?.function;
        if (possibility === 'only_verpleegkundige') return ['A1', 'A2'].includes(fn);
        if (possibility === 'only_zorgkundige') return ['A0', 'A3'].includes(fn);
        return true;
    };

    useEffect(() => {
        if (!watchedFreelancerId) return;
        const selected = freelancers.find(f => String(f.id) === String(watchedFreelancerId));
        if (selected && !matchesPossibility(selected, watchedPossibility)) {
            setValue('freelancer_id', null);
        }
    }, [watchedPossibility, watchedFreelancerId, freelancers, setValue]);

    useEffect(() => {
        if (!watchedFreelancerId || !watchedStart || !watchedEnd) {
            setAutoInterrupted(null);
            setValue('interrupted_shift', false);
            return;
        }

        let cancelled = false;
        const handle = setTimeout(async () => {
            const result = await previewInterrupted({
                branch_id: getValues('branch_id') ?? branchId,
                freelancer_id: watchedFreelancerId,
                start_time: dayjs(watchedStart).format('YYYY-MM-DD HH:mm:ss'),
                end_time: dayjs(watchedEnd).format('YYYY-MM-DD HH:mm:ss'),
            });
            if (cancelled || result === null) return;
            setAutoInterrupted(result);
            setValue('interrupted_shift', result);
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [watchedFreelancerId, watchedStart, watchedEnd, branchId, previewInterrupted, getValues, setValue]);

    const onSubmit = (data) => {
        const transformedData = {
            ...data,
            start_time: dayjs(data.start_time).format('YYYY-MM-DD HH:mm:ss'),
            end_time: dayjs(data.end_time).format('YYYY-MM-DD HH:mm:ss'),
            is_published: data.is_published === "" ? false : data.is_published,
            shift_applications_possibility: data.shift_applications_possibility ?? 'both',
            shift_repeats: repeatDates.map(date => dayjs(date).format('YYYY-MM-DD')) ?? [],
            freelancer_id: data?.freelancer_id ?? null,
        };

        if (data?.freelancer_id && typeof autoInterrupted === 'boolean' && data?.interrupted_shift !== autoInterrupted) {
            transformedData.interrupted_shift = data.interrupted_shift;
        } else {
            delete transformedData.interrupted_shift;
        }

        if (!transformedData.freelancer_id) {
            delete transformedData.freelancer_id;
        }

        if (!data?.shift_repeats_enabled) {
            delete transformedData.shift_repeats;
        }

        setIsCreating(true);
        createShift(transformedData)
            .then(() => {
                toast.success(t('shifts.toast_created'));
                reset({
                    branch_id: branchId !== null ? branchId : null,
                    freelancer_id: null,
                    start_time: null,
                    end_time: null,
                    break_duration: null,
                    is_published: false,
                    remarks: null,
                    shift_repeats_enabled: false,
                    shift_repeats: [],
                    shift_applications_possibility: 'both',
                    allowed_statuten: ['freelance'],
                    interrupted_shift: false,
                });
                setRepeatDates([]);
                onCreated();
                onClose();
            })
            .catch((error) => {
                console.error('Error creating shift', error);
            })
            .finally(() => {
                setIsCreating(false);
            });
    }

    return (
        <Dialog className="relative z-50" open={open} onClose={onClose}>
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0"
            />

            <div className="fixed inset-0 overflow-hidden">
                <div className="absolute inset-0 overflow-hidden">
                    <div className="pointer-events-none p-4 rounded-lg fixed inset-y-0 right-0 flex max-w-full">
                        <DialogPanel
                            transition
                            className="pointer-events-auto w-screen max-w-lg transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
                        >
                            <div className="flex h-full rounded-2xl flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                                <div className="px-4 sm:px-6">
                                    <div className="flex items-start justify-between">
                                        <DialogTitle className="text-base font-semibold leading-6 text-gray-900">
                                            {t('shifts.dialog_create_shift_title')}
                                        </DialogTitle>
                                        <div className="ml-3 flex h-7 items-center">
                                            <button
                                                type="button"
                                                className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                                                onClick={() => onClose()}
                                            >
                                                <span className="absolute -inset-2.5" />
                                                <span className="sr-only">Close panel</span>
                                                <FontAwesomeIcon icon={faTimes} className="h-6 w-6" aria-hidden="true" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="relative mt-6 flex-1 px-4 sm:px-6">
                                    <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
                                        <div className="mb-4">
                                            <div className={'mb-2'}>
                                                <label htmlFor={name}
                                                    className="block text-left text-sm font-medium leading-6 text-gray-900 mb-2">
                                                    Vestiging
                                                </label>

                                                <p>
                                                    {customerBranches?.filter((branch => branch.id === branchId))[0]?.label ?? '-'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="mb-2">
                                            <SelectInput
                                                name={'freelancer_id'}
                                                options={freelancers
                                                    .filter((freelancer) => matchesPossibility(freelancer, watchedPossibility))
                                                    .map((freelancer) => {
                                                        return {
                                                            id: freelancer.id,
                                                            value: freelancer.id,
                                                            label: `${freelancer.firstname} ${freelancer.lastname}`
                                                        }
                                                    })}
                                                helperText={t('shifts.freelancer_select_helper_short')}
                                                register={register}
                                                control={control}
                                                value={getValues('freelancer_id') ?? null}
                                                setValue={setValue}
                                                translationIndex={'shifts_module.sections_freelancer_heading'}
                                                placeholder={t('shifts.freelancer')}
                                                clearable={true}
                                            />
                                        </div>

                                        <div className={'grid grid-cols-1 md:grid-cols-2 mb-1 gap-4'}>
                                            <DateTimePickerInput
                                                label={'Start time'}
                                                name={'start_time'}
                                                register={register}
                                                setValue={setValue}
                                                translationIndex={'general.fields.start_time'}
                                                errors={errors}
                                            />
                                            <DateTimePickerInput
                                                label={'End time'}
                                                name={'end_time'}
                                                register={register}
                                                setValue={setValue}
                                                translationIndex={'general.fields.end_time'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <SelectInput
                                                name={'shift_applications_possibility'}
                                                options={shiftApplicationPossibilities}
                                                setValue={setValue}
                                                control={control}
                                                value={getValues('shift_applications_possibility') ?? null}
                                                placeholder={t('general.fields.shift_applications_possibility')}
                                                translationIndex={'shifts.shift_applications_possibility'}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <SelectInput
                                                name={'allowed_statuten'}
                                                options={statutenOptions}
                                                multiple={true}
                                                setValue={setValue}
                                                control={control}
                                                currentValue={getValues('allowed_statuten') ?? ['freelance']}
                                                translationIndex={'shifts.allowed_statuten'}
                                                helperText={t('shifts.allowed_statuten_helper')}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <NumberInput
                                                register={register}
                                                name={'break_duration'}
                                                translationIndex={'general.fields.break_duration'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <TextAreaInput
                                                register={register}
                                                name={'remarks'}
                                                translationIndex={'general.fields.remarks'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <ControlledSwitch
                                                control={control}
                                                name={'is_published'}
                                                getValues={getValues}
                                                setValue={setValue}
                                                translationIndex={'general.fields.is_published'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <div className="flex flex-row justify-start">
                                                <ControlledSwitch
                                                    control={control}
                                                    name={'shift_repeats_enabled'}
                                                    getValues={getValues}
                                                    setValue={setValue}
                                                    translationIndex={'shifts.dialog_repeat_shifts'}
                                                    errors={errors}
                                                />

                                            </div>
                                            {
                                                watchedRepeatShifts && (
                                                    <div className="mt-4">
                                                        <DatePicker
                                                            selectedDates={repeatDates}
                                                            className={'w-full mt-4'}
                                                            inline={true}
                                                            locale={nl}
                                                            selectsMultiple={true}
                                                            shouldCloseOnSelect={false}
                                                            disabledKeyboardNavigation={true}
                                                            onChange={(dates) => {
                                                                setRepeatDates(dates);
                                                            }}
                                                            minDate={new Date()}
                                                        />

                                                    </div>
                                                )
                                            }
                                        </div>
                                        <div className="mb-2">
                                            <div className="flex flex-row items-center gap-2">
                                                <ControlledSwitch
                                                    control={control}
                                                    name={'interrupted_shift'}
                                                    getValues={getValues}
                                                    setValue={setValue}
                                                    translationIndex={'shifts.interrupted_shift'}
                                                    errors={errors}
                                                    disabled={autoInterrupted === null}
                                                />
                                                {autoInterrupted !== null ? (
                                                    <>
                                                        <span
                                                            className={
                                                                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ' +
                                                                (showAsManual ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700')
                                                            }
                                                            title={showAsManual
                                                                ? t('shifts.interrupted_shift_tooltip_manual')
                                                                : t('shifts.interrupted_shift_tooltip_auto')}
                                                        >
                                                            {showAsManual
                                                                ? t('shifts.interrupted_shift_source_manual')
                                                                : t('shifts.interrupted_shift_source_auto')}
                                                        </span>
                                                        {showAsManual && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setValue('interrupted_shift', autoInterrupted)}
                                                                className="text-gray-500 hover:text-gray-800 text-xs"
                                                                title={t('shifts.interrupted_shift_reset')}
                                                            >
                                                                <FontAwesomeIcon icon={faRotateLeft} />
                                                            </button>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="text-sm text-gray-500">
                                                        {!watchedFreelancerId
                                                            ? t('shifts.interrupted_shift_requires_freelancer')
                                                            : t('shifts.interrupted_shift_requires_times')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <PrimaryButton
                                            onClick={handleSubmit(onSubmit)}
                                            disabled={isCreating}
                                            className={isCreating ? 'opacity-50 cursor-not-allowed' : ''}
                                        >
                                            {isCreating ? (
                                                <span className="flex items-center justify-center">
                                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    {t('shifts.creating')}
                                                </span>
                                            ) : (
                                                'Shift aanmaken'
                                            )}
                                        </PrimaryButton>
                                    </form>
                                </div>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}